<script setup lang="ts">
import { RouterLink } from 'vue-router';
import { Button } from '@/components/ui/button';
import { useHead } from '@unhead/vue';
import ScrollReveal from '@/components/ui/animation/ScrollReveal.vue';
import {
  APP_CTA_TO,
  APP_CTA_LABEL,
  TELEGRAM_CTA_LABEL,
  TELEGRAM_BOT_URL,
} from '@app/presentation/components/site/constants';
import PaperCutout from '@app/presentation/components/paper/PaperCutout.vue';
import PaperLetter from '@app/presentation/components/paper/PaperLetter.vue';
import SeoCrumbs from '@app/presentation/components/seo/SeoCrumbs.vue';
import SeoFinalCta from '@app/presentation/components/seo/SeoFinalCta.vue';

const METHOD_TITLE = 'Методика Таро Мяу — Как Мы Составляем Значения Карт | Таро Мяу';
const METHOD_DESCRIPTION =
  'Как Таро Мяу составляет толкования карт: система Райдера–Уэйта–Смита, ассоциации Золотой Зари, многоступенчатая проверка на соответствие канону и честный взгляд на то, чем таро является, а чем — нет.';

useHead({
  title: METHOD_TITLE,
  meta: [
    { name: 'description', content: METHOD_DESCRIPTION },
    {
      name: 'keywords',
      content:
        'методика таро, как составлены значения карт, система райдера уэйта смита, золотая заря таро, таро это саморефлексия, толкование карт таро источники',
    },
    { property: 'og:title', content: METHOD_TITLE },
    { property: 'og:description', content: METHOD_DESCRIPTION },
    { property: 'og:image', content: 'https://tarotmeow.ru/og-image.jpg' },
    { property: 'og:locale', content: 'ru_RU' },
    { property: 'og:type', content: 'article' },
    { property: 'og:site_name', content: 'Таро Мяу' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: METHOD_TITLE },
    { name: 'twitter:description', content: METHOD_DESCRIPTION },
    { name: 'twitter:image', content: 'https://tarotmeow.ru/og-image.jpg' },
  ],
  link: [
    { rel: 'canonical', href: 'https://tarotmeow.ru/karty/metodika' },
    { rel: 'alternate', hreflang: 'ru', href: 'https://tarotmeow.ru/karty/metodika' },
    { rel: 'alternate', hreflang: 'x-default', href: 'https://tarotmeow.ru/karty/metodika' },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Главная', item: 'https://tarotmeow.ru/' },
          { '@type': 'ListItem', position: 2, name: 'Карты', item: 'https://tarotmeow.ru/karty' },
          { '@type': 'ListItem', position: 3, name: 'Методика', item: 'https://tarotmeow.ru/karty/metodika' },
        ],
      }),
    },
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: 'Методика Таро Мяу',
        description: METHOD_DESCRIPTION,
        url: 'https://tarotmeow.ru/karty/metodika',
        inLanguage: 'ru-RU',
        publisher: {
          '@type': 'Organization',
          name: 'Таро Мяу',
          alternateName: 'Tarot Meow',
          url: 'https://tarotmeow.ru/',
        },
        mainEntity: {
          '@type': 'Article',
          headline: 'Как мы составляем значения карт Таро',
          inLanguage: 'ru-RU',
          about: 'Методика толкования карт Таро на основе системы Райдера–Уэйта–Смита',
          publisher: {
            '@type': 'Organization',
            name: 'Таро Мяу',
            alternateName: 'Tarot Meow',
            url: 'https://tarotmeow.ru/',
          },
        },
      }),
    },
  ],
});

// Источники, на которых стоят наши толкования — рендерятся в HTML для прозрачности.
const sources = [
  {
    title: 'Система Райдера–Уэйта–Смита',
    body: 'Основа всех значений на сайте — классическая колода Райдера–Уэйта–Смита (RWS, 1909). Это самый изученный и узнаваемый «язык» Таро: именно его символику — сцены на картах, цвета, жесты, числа — мы берём за каноническую отправную точку для каждой из 78 карт.',
  },
  {
    title: 'Ассоциации Золотой Зари',
    body: 'Поверх RWS мы аккуратно используем соответствия герметического ордена «Золотая Заря»: стихии, астрологию и нумерологию мастей и арканов. Эти связи помогают объяснить, почему карта значит именно то, что значит, — а не просто заучить толкование.',
  },
  {
    title: 'Контекст и здравый смысл',
    body: 'Значение карты живёт в вопросе. Поэтому к каждой карте мы даём разбор по сферам — любовь, работа, совет — и отдельно показываем перевёрнутые оттенки. Так толкование остаётся гибким, а не превращается в один жёсткий «приговор».',
  },
];

const principles = [
  {
    title: 'Соответствие RWS',
    body: 'Каждое значение мы сверяем с канонической символикой Райдера–Уэйта–Смита: что изображено на карте, какой стихии и числу она принадлежит. Если толкование расходится с источником — оно переписывается.',
  },
  {
    title: 'Проверка на непротиворечивость',
    body: 'Каждое толкование проходит многоступенчатую проверку на точность и внутреннюю непротиворечивость: прямое и перевёрнутое значения, любовь, работа и совет должны складываться в единый образ карты, а не спорить друг с другом.',
  },
  {
    title: 'Без оккультных обещаний',
    body: 'Мы не обещаем «100% сбычу» и не пугаем. Карты описываются как образы и тенденции, а не как неизбежные события. Там, где в народе живёт миф о карте, мы честно отделяем миф от значения.',
  },
];

const howToUse = [
  'Сформулируй вопрос так, чтобы ответ что-то менял в твоих действиях, а не просто «да/нет о будущем».',
  'Открой нужную карту в разделе «Карты» и прочитай прямое значение — это базовый образ.',
  'Посмотри сферу, которая тебе близка: любовь, работа или совет — толкование подстроится под контекст.',
  'Если карта легла перевёрнутой, читай раздел перевёрнутых оттенков как смещение акцента, а не «противоположность».',
  'Хочешь живой разбор под свою ситуацию — собери расклад: кот соберёт карты в связную историю.',
];
</script>

<template>
  <article class="pb-4">
    <!-- ============================ HERO ============================ -->
    <section class="pt-6 pb-14 md:pt-10 md:pb-20">
      <div class="site-container">
        <SeoCrumbs :items="[{ label: 'Главная', to: '/' }, { label: 'Карты', to: '/karty' }, { label: 'Методика' }]" />

        <header class="mx-auto max-w-[760px] text-center">
          <div class="type-overline mb-3.5 text-primary-ink">Как мы работаем</div>
          <h1 class="type-display">Методика Таро Мяу</h1>
          <p class="type-body mx-auto mt-4 max-w-2xl text-muted-foreground md:text-[17px]">
            Откуда берутся значения карт на этом сайте, кто их проверяет и почему мы говорим о Таро
            честно — как об инструменте саморефлексии, а не как о предсказании судьбы.
          </p>
        </header>
      </div>
    </section>

    <!-- ===================== КАК СОСТАВЛЯЕМ ЗНАЧЕНИЯ ===================== -->
    <section class="pb-14 md:pb-20">
      <div class="site-container">
        <ScrollReveal class="mb-8">
          <div class="type-overline mb-3.5 text-primary-ink">Источники</div>
          <h2 class="type-title">Как мы составляем значения</h2>
          <p class="type-body mt-3.5 max-w-[760px] text-muted-foreground">
            Толкования не выдуманы «на ходу». Они опираются на проверенную столетием традицию, а уже
            поверх неё мы пишем тёплым человеческим языком.
          </p>
        </ScrollReveal>

        <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
          <ScrollReveal
            v-for="(source, i) in sources"
            :key="source.title"
            :delay="((i % 3) + 1) * 80"
          >
            <div class="paper-sheet flex h-full flex-col p-5 md:p-6">
              <div class="seo-disc type-app-heading h-11 w-11">
                {{ String(i + 1).padStart(2, '0') }}
              </div>
              <h3 class="type-subheading mt-4 text-foreground">{{ source.title }}</h3>
              <p class="type-body mt-2.5 text-muted-foreground">{{ source.body }}</p>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>

    <!-- ========================= КТО ПРОВЕРЯЕТ ========================= -->
    <section class="pb-14 md:pb-20">
      <div class="site-container">
        <ScrollReveal class="mb-8">
          <div class="type-overline mb-3.5 text-primary-ink">Контроль качества</div>
          <h2 class="type-title">Как мы проверяем</h2>
          <p class="type-body mt-3.5 max-w-[760px] text-muted-foreground">
            Каждый текст значения проходит многоступенчатую проверку на соответствие канону
            Райдера–Уэйта–Смита — сверяем символику, стихии и числа, дорабатываем формулировки
            и убираем шаблонные повторы. Для нас важно одно: чтобы значение было точным, полезным
            и узнаваемым.
          </p>
        </ScrollReveal>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <ScrollReveal
            v-for="(principle, i) in principles"
            :key="principle.title"
            :delay="((i % 3) + 1) * 80"
          >
            <div class="paper-sheet flex h-full flex-col p-5 md:p-6">
              <h3 class="type-app-heading text-foreground">{{ principle.title }}</h3>
              <p class="type-body mt-2.5 text-muted-foreground">{{ principle.body }}</p>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>

    <!-- ====================== ГОЛОС ПРОВОДНИКА-КОТА ====================== -->
    <!-- A velvet patch — and the guide himself, lying there calm and warm. -->
    <section class="pb-14 md:pb-20">
      <div class="site-container">
        <ScrollReveal>
          <div class="paper-sheet fabric-velvet relative flex flex-col overflow-hidden bg-stock-plum px-7 pt-9 text-foreground md:flex-row md:items-end md:gap-8 md:p-12 md:pb-0">
            <div class="relative z-[1] flex-1 md:pb-12">
              <div class="type-overline mb-3.5 text-foreground/80">Тон голоса</div>
              <h2 class="type-title mb-4">Голос проводника-кота</h2>
              <p class="type-body max-w-[760px] md:text-[17px]">
                За каждым толкованием — один проводник: спокойный, тёплый кот. Он не вещает свысока и не
                давит на тревогу. Он садится рядом, смотрит на карту вместе с тобой и помогает заметить
                то, что ты и так уже чувствуешь, но не успел назвать словами.
              </p>
              <p class="type-body mt-4 max-w-[760px] md:text-[17px]">
                Поэтому язык на сайте — простой и бережный. Мы избегаем угроз, фатализма и
                «магических» обещаний. Цель кота — не напугать предсказанием, а вернуть тебе ясность и
                чуть больше доброты к себе.
              </p>
            </div>
            <div
              class="relative z-[1] mb-6 mt-4 w-[240px] self-center md:mb-10 md:mt-0 md:w-[280px] md:shrink-0 md:self-end"
              aria-hidden="true"
            >
              <PaperCutout
                name="belly_cat"
                still
                lazy
              />
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>

    <!-- ======================= ЧЕСТНЫЙ ДИСКЛЕЙМЕР ======================= -->
    <!-- A taped note. -->
    <section class="pb-14 md:pb-20">
      <div class="site-container">
        <ScrollReveal class="mx-auto max-w-[820px]">
          <PaperLetter>
            <div class="type-overline mb-3.5 text-primary-ink">Честно о главном</div>
            <h2 class="type-title">Чем Таро является, а чем — нет</h2>
            <p class="letter-text type-body mt-4 text-muted-foreground md:text-[17px]">
              Таро — это повод задуматься и инструмент саморефлексии, а не предсказание судьбы. Карты не
              решают за тебя и не отменяют твоей свободы выбора. Они — зеркало: помогают увидеть
              ситуацию под другим углом, назвать чувства и вернуть себе ответственность за решение.
            </p>
            <p class="letter-text type-body mt-4 text-muted-foreground md:text-[17px]">
              Толкования на сайте носят развлекательный и психологический характер. Они не заменяют
              консультацию врача, психолога, юриста или финансового специалиста. Если тебе тяжело —
              пожалуйста, обратись к живому человеку, которому доверяешь.
            </p>
          </PaperLetter>
        </ScrollReveal>
      </div>
    </section>

    <!-- ===================== КАК ПОЛЬЗОВАТЬСЯ РАЗДЕЛОМ ===================== -->
    <section class="pb-14 md:pb-20">
      <div class="site-container">
        <ScrollReveal class="mb-8">
          <div class="type-overline mb-3.5 text-primary-ink">Шаг за шагом</div>
          <h2 class="type-title">Как пользоваться разделом</h2>
        </ScrollReveal>

        <div class="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
          <ScrollReveal
            v-for="(step, i) in howToUse"
            :key="i"
            :delay="((i % 2) + 1) * 80"
            class="me-step flex gap-4 pt-5"
          >
            <span class="seo-disc type-app-heading h-10 w-10">{{ String(i + 1).padStart(2, '0') }}</span>
            <p class="type-body text-muted-foreground">{{ step }}</p>
          </ScrollReveal>
        </div>
      </div>
    </section>

    <!-- ========================= BOTTOM CTA ======================== -->
    <div class="site-container py-6 md:py-10">
      <ScrollReveal>
        <SeoFinalCta>
          <h2 class="type-title">Хочешь примерить это на себя?</h2>
          <p class="type-body mx-auto mb-8 mt-[18px] max-w-[560px] text-muted-foreground md:text-lg">
            Загляни в значения карт или собери живой расклад — кот соберёт их в историю под твой вопрос.
          </p>
          <div class="flex flex-col items-center justify-center gap-3 sm:flex-row sm:flex-wrap">
            <Button
              as-child
              size="lg"
            >
              <RouterLink :to="APP_CTA_TO">{{ APP_CTA_LABEL }}</RouterLink>
            </Button>
            <Button
              as-child
              variant="outline"
              size="lg"
            >
              <a
                :href="TELEGRAM_BOT_URL"
                target="_blank"
                rel="noopener"
              >{{ TELEGRAM_CTA_LABEL }}</a>
            </Button>
            <Button
              as-child
              variant="ghost"
              size="lg"
            >
              <RouterLink to="/karty">Все карты</RouterLink>
            </Button>
            <Button
              as-child
              variant="ghost"
              size="lg"
            >
              <RouterLink to="/rasklady">Расклады</RouterLink>
            </Button>
          </div>
        </SeoFinalCta>
      </ScrollReveal>
    </div>
  </article>
</template>

<style scoped src="../components/seo/seo-paper.css"></style>

<style scoped>
.me-step {
  border-top: 1px dashed rgb(242 235 221 / 0.14);
}
</style>
